import { useState, useEffect, useCallback, memo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
// Need to use Button and ButtonGroup from MUI for design consistency
import { Input, InputAdornment, Button, ButtonGroup } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { makeStyles } from "@mui/styles";
import { Theme } from "@mui/material/styles";
import { GridRenderCellParams } from "@mui/x-data-grid";
import moment from "moment";
import { debounce, startCase } from "lodash";
import clsx from "clsx";
import { Switch, FormControl, Typography, Grid, IconButton } from "@financeable-com-au/financeable-ui";
import { DEFAULT_SORT_FIELD, SERVICE_TYPES } from "@/constants";
import {
  saveValue,
  serviceStateSelector,
  getAllServices,
  updateServiceTransactionChargesStatus,
} from "@/store/slices/serviceSlice";
import { ServiceRow, GetAllServicesFnArgs, IServiceSlice, IServiceState } from "@/store/slices/types/serviceSliceTypes";
import { AppDispatch } from "@/store/store";
import { fCurrencyDecimal } from "@/utils/formatNumber";
// @ts-expect-error: ignoring as it is existing js file
import DataGridTable from "../../dataGridTable/DataGridTable";

export const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  borderRemove: {
    "& fieldset": {
      border: "0px !important",
    },
  },
  margin: {
    margin: theme.spacing(1),
  },
  withoutLabel: {
    marginTop: theme.spacing(3),
  },
  textField: {
    width: "61ch",
  },
}));

const ServiceList = memo(() => {
  const classes = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const pageSizeRef = useRef(10);
  const serviceTypeFilterRef = useRef<string>("All");
  const serviceState = useSelector<IServiceSlice, IServiceState>(serviceStateSelector);
  const { services, gettingServices, page } = serviceState;
  const [rows, setRows] = useState<ServiceRow[]>([]);
  const [updatingCells, setUpdatingCells] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [rowCountState, setRowCountState] = useState(services?.totalDocs || 0);
  const [params, setParams] = useState({ offset: 1, limit: 10, sort: DEFAULT_SORT_FIELD } as {
    offset: number;
    limit: number;
    sort: { field: string; sort: "asc" | "desc" };
  });
  const servicesTypeFilters = ["All", SERVICE_TYPES.paid, SERVICE_TYPES.free];

  const handle = {
    getAllServicesFn: ({
      cursor = 1,
      search,
      filter = serviceTypeFilterRef.current || "All",
    }: GetAllServicesFnArgs) => {
      const sort = params?.sort || DEFAULT_SORT_FIELD;
      dispatch(getAllServices({ cursor, limit: pageSizeRef?.current || 10, search, filter, sort }));
    },
    handleSearch: (searchTerm: string) => {
      (searchTerm.length >= 2 || searchTerm.length <= 0) &&
        handle.getAllServicesFn({ cursor: services?.page, search: searchTerm });
    },
    handleServiceTypeFilterChange: async (option: string) => {
      handle.getAllServicesFn({
        cursor: 1,
        search: searchTerm,
        filter: option,
      });
      serviceTypeFilterRef.current = option;
    },
    handleTransactionChargesToggle: async (serviceId: string, value: boolean) => {
      // Guard against a second request for the same cell while one is in flight.
      if (updatingCells[serviceId]) return;
      setUpdatingCells((prev) => ({ ...prev, [serviceId]: true }));
      try {
        await dispatch(updateServiceTransactionChargesStatus({ serviceId, value })).unwrap();
      } catch {
        // Request failed - the slice keeps the previous value, so the toggle reverts.
      } finally {
        setUpdatingCells((prev) => {
          const next = { ...prev };
          delete next[serviceId];
          return next;
        });
      }
    },
  };


  const servicesColumns = [
    {
      field: "createdAt",
      headerName: "Date",
      type: "string",
      width: 150,
      editable: false,
      renderCell: (params: GridRenderCellParams<{ date: string; time: string }>) => (
        <div>
          <Typography style={{ fontSize: "14px" }}>{params.value?.date}</Typography>
          <Typography textColor="neutral.500" style={{ fontSize: "11px" }}>
            {params.value?.time}
          </Typography>
        </div>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      width: 350,
    },
    {
      field: "fee",
      headerName: "Fees",
      width: 200,
      renderCell: (params: GridRenderCellParams<number>) => (
        <Typography style={{ fontSize: "14px" }}>{fCurrencyDecimal((params.value ?? 0) / 100)}</Typography>
      ),
    },
    {
      field: "is_transaction_charges_applicable",
      headerName: "Transaction Charges Applicable",
      width: 200,
      sortable: false,
      renderCell: (params: GridRenderCellParams<boolean>) => {
        const serviceId = String(params.row.id);
        const isUpdating = !!updatingCells[serviceId];
        return (
          <Switch
            checked={!!params.value}
            variant={params.value ? "solid" : "outlined"}
            disabled={isUpdating}
            onChange={(event) => handle.handleTransactionChargesToggle(serviceId, event.target.checked)}
          />
        );
      },
    },
  ];

  useEffect(() => {
    setRowCountState((prevRowCountState) => services?.totalDocs ?? prevRowCountState);
  }, [services?.totalDocs]);

  useEffect(() => {
    if (Array.isArray(services?.list)) {
      const servicesRows = services.list.map((doc) => {
        const createdAt = doc?.createdAt ? new Date(doc?.createdAt) : "";
        return {
          id: doc?._id,
          name: doc?.name || "-",
          cursor: services?.page,
          fee: doc?.fee ?? 0,
          is_transaction_charges_applicable: doc?.is_transaction_charges_applicable || false,
          createdAt: createdAt
            ? {
                date: moment(createdAt).format("D-M-YY"),
                time: moment(createdAt).format("h:mm a"),
              }
            : {
                date: "",
                time: "",
              },
        };
      });

      setRows(servicesRows);
    } else {
      setRows([]);
    }
  }, [services]);

  useEffect(() => {
    if (params?.offset) {
      handle.getAllServicesFn({
        cursor: params?.offset,
        search: searchTerm,
        filter: serviceTypeFilterRef.current,
      });
      dispatch(saveValue({ page: params?.offset, limit: params?.limit }));
    }
  }, [params?.offset, params?.sort, params?.limit]);

  const debounced = useCallback(debounce(handle.handleSearch, 500), []);

  return (
    <Grid container direction="column" spacing={0} style={{ padding: `20px` }}>
      <Grid
        style={{
          borderBottom: "1px solid rgba(224, 224, 224, 1)",
          display: "flex",
          alignItems: "center",
          columnGap: "50px",
        }}
      >
        <Typography
          level="h1"
          style={{
            fontSize: "24px",
            fontWeight: 800,
            letterSpacing: "-0.5px",
          }}
        >
          Services
        </Typography>
        <div className={classes.root}>
          <FormControl className={clsx(classes.margin, classes.textField)}>
            <Input
              className="input-border-bottom"
              id="service-search"
              autoComplete="search"
              placeholder="Search…"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                debounced(e.target.value);
              }}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              }
              endAdornment={
                searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setSearchTerm("");
                        searchTerm !== "" && debounced("");
                      }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }
            />
          </FormControl>
        </div>
        <ButtonGroup variant="outlined">
          {servicesTypeFilters.map((option) => (
            <Button
              key={option}
              size="small"
              onClick={() => {
                handle.handleServiceTypeFilterChange(option);
              }}
              variant={serviceTypeFilterRef.current === option ? "contained" : "outlined"}
            >
              {startCase(option)}
            </Button>
          ))}
        </ButtonGroup>
      </Grid>

      <Grid>
        <DataGridTable
          onCellClick={() => {}}
          onRowClick={() => {}}
          data={rows || []}
          columns={servicesColumns}
          rowsPerPageOptions={[10, 20, 50, 100]}
          pageSize={pageSizeRef?.current || 10}
          rowCount={rowCountState}
          params={params}
          setParams={setParams}
          page={page}
          isLoading={gettingServices}
          pageSizeRef={pageSizeRef}
          disableColumnFilter
        />
      </Grid>
    </Grid>
  );
});

export default ServiceList;
